import { tokens } from './tokens'
import type { Theme, ThemeOverrides } from './types'

type PlainObject = Record<string, unknown>

const isPlainObject = (value: unknown): value is PlainObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

// Local instead of structuredClone: bare React Native never installs
// structuredClone as a global (only Expo's winter runtime polyfills it), and
// this module must stay safe on every platform the tokens are.
function deepClone<T extends PlainObject>(source: T): T {
  const result: PlainObject = {}
  for (const [key, value] of Object.entries(source)) {
    result[key] = isPlainObject(value) ? deepClone(value) : value
  }
  return result as T
}

function deepMerge<T extends PlainObject>(base: T, overrides: PlainObject): T {
  const result: PlainObject = { ...base }
  for (const key of Object.keys(overrides)) {
    const overrideValue = overrides[key]
    if (overrideValue === undefined) continue
    const baseValue = result[key]
    result[key] =
      isPlainObject(baseValue) && isPlainObject(overrideValue) ? deepMerge(baseValue, overrideValue) : overrideValue
  }
  return result as T
}

/**
 * Builds a resolved theme from the unified tokens, deep-merging optional
 * overrides. This is the white-label extension point: a brand (or an app
 * preserving legacy colors during migration) overrides only the tokens it
 * needs and inherits the rest.
 *
 * Pure function — never mutates `tokens` or the overrides, and the result is
 * a deep copy so a consumer mutating its theme cannot corrupt the shared
 * token singleton.
 */
export function createTheme(overrides?: ThemeOverrides): Theme {
  const base: Theme = deepClone(tokens)
  return deepMerge(base, overrides ?? {})
}
